// A4 — the section drop-off map (PRD 16 §4). *"41% of the visits that reach
// step 3 go no further."* The cheapest Tier-A item, and the one docs teams
// already report on, which is why it needs no education to sell.
//
// It is also the panel most likely to be quoted out of context, so four things
// here are deliberate and should not be "tidied":
//
//  1. **The end of the document is not a drop-off.** A visit that reaches the
//     last section and stops has finished reading. Ranked naively by exit
//     share that row wins on every document ever written, so it is labelled as
//     the end and sorted below every real drop-off (`rankStops`).
//  2. **`not_measured` rows are rendered with the backend's reason, and get no
//     bar.** A meter drawn at zero for a section nobody reached is exactly the
//     0%-versus-not-measured lie the rest of this screen avoids.
//  3. **Every share names its denominator.** "of the visits that reached it" and
//     "of visits reached it" are different numbers with the same shape.
//  4. **"went no further", never "abandoned".** There is no `section_leave`
//     event; an exit is the last section a visit was seen viewing. The panel
//     reports where reading stopped and says nothing about why.

import { Card, EmptyState, MeterBar } from "../../ui";
import type { SectionDropOffMap, SectionStop } from "./api";
import { formatCount, formatShare, shownOf } from "./format";
import {
  DROPOFF_EMPTY_BODY,
  DROPOFF_EMPTY_HEADLINE,
  DROPOFF_ENDS_HERE,
  DROPOFF_EXIT_WHY,
  DROPOFF_NO_VERDICT_WHY,
  DROPOFF_SUBTITLE,
  DROPOFF_TITLE,
  DROPOFF_UNORDERED,
  REACHED_LABEL,
  SESSIONS_LABEL,
  WENT_NO_FURTHER,
} from "./metricLabels";
import "./analytics.css";

const MAX_ROWS = 6;

/**
 * Worst real drop-off first; the document's own ending after them; sections too
 * thin to judge last.
 *
 * The two group keys come before the share for a reason. `ends_here` routinely
 * carries the highest exit share in the document and means the opposite of a
 * problem, and a `not_measured` row has no share at all — sorting on the number
 * alone would put the conclusion at the top and the unjudged rows in the middle
 * with nothing to compare them by.
 */
export function rankStops(sections: SectionStop[]): SectionStop[] {
  return [...sections].sort(
    (a, b) =>
      Number(a.verdict === "not_measured") - Number(b.verdict === "not_measured") ||
      Number(a.verdict === "ends_here") - Number(b.verdict === "ends_here") ||
      (b.exit_share ?? 0) - (a.exit_share ?? 0) ||
      (a.position ?? 0) - (b.position ?? 0),
  );
}

function label(stop: SectionStop): string {
  return stop.heading ?? stop.section_id;
}

/** "step 3", or the honest alternative when the events carry a heading slug the
 *  current document no longer has. */
function place(stop: SectionStop): string {
  return stop.position === null ? "no longer in this document" : `step ${stop.position}`;
}

export type SectionDropOffProps = { map: SectionDropOffMap };

export function SectionDropOff({ map }: SectionDropOffProps) {
  const ranked = rankStops(map.sections);
  const shown = ranked.slice(0, MAX_ROWS);
  const where = map.doc_title ?? map.doc_id;

  return (
    <Card
      header={DROPOFF_TITLE}
      footer={
        <span className="type-bodyS">
          {map.sections.length === 0
            ? DROPOFF_SUBTITLE
            : `${shownOf(shown.length, ranked.length, "section")} of ${where} · ${map.sections_measured} measured`}
        </span>
      }
    >
      {map.sections.length === 0 ? (
        <EmptyState
          headline={DROPOFF_EMPTY_HEADLINE}
          body={DROPOFF_EMPTY_BODY}
        />
      ) : (
        <>
          {map.sections_ordered ? null : (
            <p className="maisight-analytics-dropoff-note type-bodyS">
              {DROPOFF_UNORDERED}
            </p>
          )}
          <ul
            className="maisight-analytics-meters"
            aria-label="Section drop-off by section"
          >
            {shown.map((stop) => {
              const measured = stop.verdict !== "not_measured";
              return (
                <li key={stop.section_id} className="maisight-analytics-meter-row">
                  <div className="maisight-analytics-meter-head">
                    <span className="maisight-analytics-term type-numSm">
                      {label(stop)}
                    </span>
                    <span className="type-numSm maisight-analytics-muted">
                      {place(stop)}
                    </span>
                  </div>
                  {/* No bar for a row with no share. A meter at zero reads as a
                    *  measured zero, which is the claim this panel must not make. */}
                  {measured ? (
                    <MeterBar
                      value={Math.round((stop.exit_share ?? 0) * 100)}
                      max={100}
                      tone={stop.verdict === "drop_off" ? "flagged" : "nominal"}
                      label={`${formatShare(stop.exit_share)} of the visits that reached ${label(stop)} ${WENT_NO_FURTHER}`}
                    />
                  ) : null}
                  <p className="maisight-analytics-dropoff-meta type-bodyS">
                    <span
                      className="maisight-analytics-metric"
                      title={measured ? DROPOFF_EXIT_WHY : undefined}
                      data-measured={measured ? "yes" : "no"}
                    >
                      {measured
                        ? `${formatShare(stop.exit_share)} of the ${formatCount(stop.sessions_reached)} ${SESSIONS_LABEL} that ${REACHED_LABEL} it ${WENT_NO_FURTHER}`
                        : `${DROPOFF_NO_VERDICT_WHY} ${stop.not_measured_reason ?? ""}`}
                    </span>
                    {stop.verdict === "ends_here" ? (
                      <span className="maisight-analytics-dropoff-end">
                        {DROPOFF_ENDS_HERE}
                      </span>
                    ) : null}
                    <span>
                      {`${formatShare(stop.reach_share)} of visits reached it`}
                    </span>
                  </p>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </Card>
  );
}
